import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, ShieldCheck, Zap, Brain, Activity, Clock, CheckCircle2, ChevronRight, Star } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
export function App() {
  const [modalType, setModalType] = useState<'privacy' | 'terms' | null>(null);
  const CTAButton = ({
    className = '',
    text = 'SÍ, QUIERO MI COPIA DEL PROTOCOLO POR $6.39',
    onClick = () => {
      if ((window as any).fbq) {
        ;
        (window as any).fbq('track', 'AddToCart');
      }
      window.location.href = 'https://flujovital360.com/cart/46068095516868:1';
    }
  }: {
    className?: string;
    text?: string;
    onClick?: () => void;
  }) => <motion.button onClick={onClick} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} className={`group relative inline-flex items-center justify-center gap-3 bg-amber-500 px-8 py-5 text-lg font-black uppercase tracking-wider text-zinc-950 transition-all hover:bg-amber-400 hover:shadow-[0_0_40px_rgba(245,158,11,0.4)] sm:text-xl ${className}`} data-id="element-1">
      <span data-id="element-2">{text}</span>
      <ChevronRight className="h-6 w-6 transition-transform group-hover:translate-x-1" data-id="element-3" />
    </motion.button>;
  return <div className="min-h-screen overflow-x-hidden" data-id="element-4">
      {/* URGENCY BAR */}
      <div className="sticky top-0 z-50 bg-red-600 px-4 py-3 text-center text-sm font-bold tracking-wide text-white shadow-lg sm:text-base" data-id="element-5">
        <div className="flex items-center justify-center gap-2" data-id="element-6">
          <AlertTriangle className="h-5 w-5 animate-pulse" data-id="element-7" />
          <span data-id="element-8">
            PRECIO DE LANZAMIENTO: Solo por las próximas 24 horas o hasta agotar
            los 500 accesos promocionales.
          </span>
        </div>
      </div>

      {/* HERO SECTION */}
      <section className="relative px-4 pt-16 pb-16 sm:pt-24 sm:pb-20" data-id="element-9">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900 via-zinc-950 to-zinc-950" data-id="element-10"></div>

        <div className="mx-auto max-w-4xl text-center" data-id="element-11">
          <FadeIn data-id="element-12">
            <h1 className="mb-6 text-4xl font-black leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl" data-id="element-13">
              Deja de ser un{' '}
              <span className="text-amber-500 text-glow" data-id="element-14">
                "cliente de por vida"
              </span>{' '}
              del sistema médico.
            </h1>
            <p className="mb-8 text-xl font-medium text-zinc-400 sm:text-3xl" data-id="element-15">
              Recupera tu salud por el precio de un café.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-16">
            <div className="mx-auto mb-10 max-w-3xl rounded-2xl border border-zinc-800 bg-zinc-900/50 p-6 backdrop-blur-sm sm:p-8" data-id="element-17">
              <p className="text-lg leading-relaxed text-zinc-300 sm:text-xl" data-id="element-18">
                El Protocolo de 90 días para desinstalar el miedo de tu mente,
                resetear tu cuerpo y activar tu curación natural desde casa.
              </p>
            </div>
          </FadeIn>

          {/* VSL Video */}
          <FadeIn delay={0.3} data-id="element-19">
            <div className="mx-auto mb-10 max-w-3xl overflow-hidden rounded-2xl border border-zinc-800 bg-black shadow-2xl box-glow" data-id="element-20">
              <video className="w-full h-auto max-h-[80vh] object-contain" controls poster="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80&w=2000" data-id="element-21">
                <source src="https://cdn.magicpatterns.com/uploads/gLoeBgTiEGBBNv27EopS6r/VIdeo Mentalidad  New.mp4" type="video/mp4" data-id="element-22" />
                Tu navegador no soporta el elemento de video.
              </video>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-23">
            <CTAButton className="w-full sm:w-auto rounded-xl" onClick={() => document.getElementById('checkout')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-24" />
            <p className="mt-4 text-sm text-zinc-500" data-id="element-25">
              Acceso inmediato • Pago único • Garantía de 7 días
            </p>
          </FadeIn>
        </div>
      </section>

      {/* INTRO / SUB-HEADLINE */}
      <section className="border-t border-zinc-900 bg-zinc-950 px-4 py-12 sm:py-16" data-id="element-26">
        <div className="mx-auto max-w-3xl text-center" data-id="element-27">
          <FadeIn data-id="element-28">
            <p className="text-xl leading-relaxed text-zinc-400 sm:text-2xl" data-id="element-29">
              No vas a arreglar años de programación en una tarde. Pero puedes
              empezar a borrar el{' '}
              <strong className="text-white" data-id="element-30">"chip de la enfermedad"</strong>{' '}
              hoy mismo. El manual definitivo para la soberanía biológica ya
              está aquí.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 1: LA REALIDAD SIN FILTROS */}
      <section className="bg-zinc-900 px-4 py-16 sm:py-20" data-id="element-31">
        <div className="mx-auto max-w-4xl" data-id="element-32">
          <FadeIn data-id="element-33">
            <div className="mb-10 flex items-center gap-4" data-id="element-34">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 text-amber-500" data-id="element-35">
                <Activity className="h-6 w-6" data-id="element-36" />
              </div>
              <h2 className="text-3xl font-black text-white sm:text-4xl" data-id="element-37">
                LA REALIDAD SIN FILTROS
              </h2>
            </div>
          </FadeIn>

          <div className="grid gap-8 md:grid-cols-2" data-id="element-38">
            <FadeIn delay={0.1} data-id="element-39">
              <h3 className="text-2xl font-bold leading-tight text-white" data-id="element-40">
                ¿Por qué estás cansado, inflamado y dependiente de una pastilla
                a pesar de que el sistema dice que "estás bien"?
              </h3>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-41">
              <div className="space-y-6 text-lg text-zinc-400" data-id="element-42">
                <p data-id="element-43">
                  La verdad es incómoda:{' '}
                  <strong className="text-amber-500" data-id="element-44">
                    El sistema te necesita con miedo.
                  </strong>{' '}
                  Una persona que cree que su salud depende de un frasco es un
                  cliente recurrente. Una persona que entiende su Soberanía
                  Biológica es libre.
                </p>
                <p data-id="element-45">
                  Te han instalado un software de fragilidad a través de la
                  televisión y el sistema de salud tradicional. Es hora de
                  desinstalar ese código.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SECTION 2: EL PROTOCOLO DE LOS 90 DÍAS */}
      <section className="px-4 py-16 sm:py-20" data-id="element-46">
        <div className="mx-auto max-w-5xl" data-id="element-47">
          <FadeIn data-id="element-48">
            <div className="mb-12 text-center" data-id="element-49">
              <h2 className="mb-6 text-3xl font-black text-white sm:text-5xl" data-id="element-50">
                EL PROTOCOLO DE LOS 90 DÍAS
              </h2>
              <p className="mx-auto max-w-3xl text-xl text-zinc-400" data-id="element-51">
                Seamos realistas: prometerte salud en 24 horas es una estafa. Tu
                cuerpo necesita tiempo real para limpiar la química del estrés y
                regenerar tejidos. Por eso este no es un libro de consejos, es
                un <strong className="text-amber-500" data-id="element-52">PLAN DE ATAQUE</strong>:
              </p>
            </div>
          </FadeIn>

          <div className="grid gap-6 md:grid-cols-3" data-id="element-53">
            {[{
            day: 'Día 1-30',
            title: 'El Reset',
            desc: 'Cortamos el flujo de cortisol y apagamos el incendio de la inflamación crónica.',
            icon: Zap
          }, {
            day: 'Día 31-60',
            title: 'El Exilio',
            desc: "Borramos las creencias de 'víctima heredada' y los virus mentales institucionales.",
            icon: Brain
          }, {
            day: 'Día 61-90',
            title: 'Soberanía',
            desc: 'Instalamos la nueva firma biológica. Tu cuerpo aprende a sanar por su cuenta.',
            icon: ShieldCheck
          }].map((step, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-54">
                <div className="relative flex h-full flex-col rounded-2xl border border-zinc-800 bg-zinc-900/50 p-8 transition-colors hover:border-amber-500/50 hover:bg-zinc-900" data-id="element-55">
                  <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500" data-id="element-56">
                    <step.icon className="h-7 w-7" data-id="element-57" />
                  </div>
                  <div className="mb-2 text-sm font-bold uppercase tracking-wider text-amber-500" data-id="element-58">
                    {step.day}
                  </div>
                  <h3 className="mb-4 text-2xl font-bold text-white" data-id="element-59">
                    {step.title}
                  </h3>
                  <p className="text-zinc-400" data-id="element-60">{step.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* SECTION 3: LO QUE VAS A RECIBIR */}
      <section className="bg-zinc-900 px-4 py-16 sm:py-20" data-id="element-61">
        <div className="mx-auto max-w-6xl" data-id="element-62">
          <div className="grid items-center gap-12 lg:grid-cols-2" data-id="element-63">
            <FadeIn data-id="element-64">
              <div className="relative mx-auto max-w-md lg:max-w-none" data-id="element-65">
                <div className="absolute -inset-4 rounded-3xl bg-amber-500/20 blur-2xl" data-id="element-66"></div>
                <img src="https://cdn.magicpatterns.com/uploads/h8KtYjAdZsFjvRjkoNHREE/imagen_de_producto.png" alt="El Código de la Curación Prohibida" className="relative z-10 w-full rounded-2xl shadow-2xl" data-id="element-67" />
              </div>
            </FadeIn>

            <div data-id="element-68">
              <FadeIn delay={0.2} data-id="element-69">
                <h2 className="mb-8 text-3xl font-black text-white sm:text-4xl" data-id="element-70">
                  LO QUE VAS A RECIBIR
                </h2>
                <div className="space-y-6" data-id="element-71">
                  <div className="flex gap-4" data-id="element-72">
                    <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-amber-500" data-id="element-73" />
                    <div data-id="element-74">
                      <h4 className="text-xl font-bold text-white" data-id="element-75">
                        Acceso inmediato al Manual Digital:
                      </h4>
                      <p className="mt-2 text-lg text-zinc-400" data-id="element-76">
                        "EL CÓDIGO DE LA CURACIÓN PROHIBIDA: El Protocolo de 90
                        días para el Despertar de tu Cuerpo."
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4" data-id="element-77">
                    <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-amber-500" data-id="element-78" />
                    <p className="text-lg text-zinc-400" data-id="element-79">
                      Un método paso a paso, basado en epigenética y
                      neurociencia práctica, explicado de tú a tú.
                    </p>
                  </div>
                  <div className="flex gap-4" data-id="element-80">
                    <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-amber-500" data-id="element-81" />
                    <p className="text-lg text-zinc-400" data-id="element-82">
                      Recupera el mando de tu biología sin rollos raros y en
                      solo{' '}
                      <strong className="text-white" data-id="element-83">10 minutos al día</strong>.
                    </p>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="px-4 py-16 sm:py-20" data-id="element-84">
        <div className="mx-auto max-w-6xl" data-id="element-85">
          <FadeIn data-id="element-86">
            <div className="mb-12 text-center" data-id="element-87">
              <h2 className="mb-4 text-3xl font-black text-white sm:text-4xl" data-id="element-88">
                LO QUE DICEN QUIENES YA DESPERTARON
              </h2>
              <p className="text-zinc-400 text-lg" data-id="element-89">
                Personas reales que decidieron tomar el control de su biología.
              </p>
            </div>
          </FadeIn>

          <div className="grid gap-6 md:grid-cols-3" data-id="element-90">
            {[{
            name: 'Carlos M.',
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=faces',
            text: 'Llevaba 5 años saltando de especialista en especialista, siempre con una nueva pastilla para tapar el síntoma. Este protocolo me hizo entender que el problema era mi estado de alerta constante. En 60 días mi inflamación bajó drásticamente.'
          }, {
            name: 'Elena R.',
            image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=faces',
            text: "El 'chip de la enfermedad' es real. Me habían convencido de que mi fatiga crónica era mi nueva normalidad. El módulo de neurociencia práctica me voló la cabeza. Hoy tengo energía para jugar con mis hijos otra vez. Vale 100 veces lo que cuesta."
          }, {
            name: 'David G.',
            image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=faces',
            text: 'Por $6 dólares pensé que sería otro PDF genérico con consejos de dieta. Me equivoqué por completo. Es un sistema profundo para resetear el sistema nervioso. Llevo 3 semanas y por primera vez en años duermo la noche entera sin interrupciones.'
          }].map((testimonial, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-91">
                <div className="flex h-full flex-col rounded-2xl border border-zinc-800 bg-zinc-900/30 p-8" data-id="element-92">
                  <div className="mb-4 flex gap-1" data-id="element-93">
                    {[...Array(5)].map((_, j) => <Star key={j} className="h-5 w-5 fill-amber-500 text-amber-500" data-id="element-94" />)}
                  </div>
                  <p className="mb-6 flex-grow text-zinc-300 italic" data-id="element-95">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-4" data-id="element-96">
                    <img src={testimonial.image} alt={testimonial.name} className="h-12 w-12 rounded-full object-cover border border-zinc-700" data-id="element-97" />
                    <div data-id="element-98">
                      <p className="font-bold text-white" data-id="element-99">{testimonial.name}</p>
                      <p className="text-xs text-amber-500 flex items-center gap-1" data-id="element-100">
                        <CheckCircle2 className="h-3 w-3" data-id="element-101" /> Compra verificada
                      </p>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* SECTION 4: POR QUÉ SOLO $6.39? */}
      <section className="bg-zinc-900 px-4 py-16 sm:py-20" data-id="element-102">
        <div className="mx-auto max-w-3xl text-center" data-id="element-103">
          <FadeIn data-id="element-104">
            <h2 className="mb-8 text-3xl font-black text-white sm:text-5xl" data-id="element-105">
              ¿POR QUÉ SOLO <span className="text-amber-500" data-id="element-106">$6.39</span>?
            </h2>
            <div className="space-y-6 text-lg text-zinc-400 sm:text-xl" data-id="element-107">
              <p data-id="element-108">
                Sé lo que estás pensando:{' '}
                <em data-id="element-109">"Si esto es tan bueno, ¿por qué cuesta tan poco?"</em>. Es
                simple: Quiero que el precio NO sea una excusa.
              </p>
              <p data-id="element-110">
                Mi meta es ayudar a 10,000 personas este año a recuperar su
                libertad biológica. A este precio, puedes empezar tu
                transformación HOY mismo sin pedir permiso a tu billetera.
              </p>
              <p className="font-bold text-white" data-id="element-111">
                Es una inversión única para un cambio de por vida.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 5: GARANTÍA DE SOBERANÍA */}
      <section className="bg-amber-500/10 px-4 py-16 border-y border-amber-500/20" data-id="element-112">
        <div className="mx-auto max-w-4xl text-center" data-id="element-113">
          <FadeIn data-id="element-114">
            <ShieldCheck className="mx-auto mb-6 h-16 w-16 text-amber-500" data-id="element-115" />
            <h2 className="mb-6 text-3xl font-black text-white sm:text-4xl" data-id="element-116">
              GARANTÍA DE SOBERANÍA
            </h2>
            <p className="text-xl text-zinc-300" data-id="element-117">
              <strong className="text-amber-500" data-id="element-118">Pruébalo por 7 días.</strong>{' '}
              Si entras, lees las primeras páginas y sientes que no es para ti,
              te devuelvo tus $6.39 de inmediato. No quiero tu dinero si no
              estás listo para dejar de creer en la mentira de la fragilidad.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="checkout" className="px-4 py-16 sm:py-24" data-id="element-119">
        <div className="mx-auto max-w-4xl text-center" data-id="element-120">
          <FadeIn data-id="element-121">
            <h2 className="mb-10 text-4xl font-black text-white sm:text-6xl" data-id="element-122">
              TU BIOLOGÍA. <br className="sm:hidden" data-id="element-123" />
              <span className="text-amber-500" data-id="element-124">TUS REGLAS.</span>
            </h2>

            <div className="mb-8 flex flex-col items-center justify-center gap-4" data-id="element-125">
              <CTAButton className="w-full sm:w-auto rounded-xl py-6 text-xl sm:text-2xl" text="SÍ, QUIERO MI COPIA DEL PROTOCOLO POR $6.39" onClick={() => {
              if ((window as any).fbq) {
                ;
                (window as any).fbq('track', 'AddToCart');
              }
              window.location.href = 'https://flujovital360.com/cart/46068095516868:1';
            }} data-id="element-126" />
            </div>

            <div className="inline-flex flex-col items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/50 px-6 py-4 text-sm text-zinc-400" data-id="element-127">
              <p className="font-bold text-white" data-id="element-128">
                ¡Acceso inmediato por solo $6.39!
              </p>
              <div className="flex items-center gap-4" data-id="element-129">
                <span className="flex items-center gap-1" data-id="element-130">
                  <CheckCircle2 className="h-4 w-4 text-amber-500" data-id="element-131" /> Pago único
                </span>
                <span className="flex items-center gap-1" data-id="element-132">
                  <CheckCircle2 className="h-4 w-4 text-amber-500" data-id="element-133" /> Sin
                  suscripciones
                </span>
                <span className="flex items-center gap-1" data-id="element-134">
                  <CheckCircle2 className="h-4 w-4 text-amber-500" data-id="element-135" /> Descarga
                  instantánea
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-zinc-900 bg-zinc-950 py-8 text-center text-sm text-zinc-600" data-id="element-136">
        <div className="mx-auto max-w-4xl flex flex-col items-center gap-4" data-id="element-137">
          <p data-id="element-138">
            © {new Date().getFullYear()} Soberanía Biológica. Todos los
            derechos reservados.
          </p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6" data-id="element-139">
            <button onClick={() => setModalType('privacy')} className="hover:text-amber-500 transition-colors" data-id="element-140">
              Política de Privacidad
            </button>
            <button onClick={() => setModalType('terms')} className="hover:text-amber-500 transition-colors" data-id="element-141">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* MODAL */}
      {modalType && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setModalType(null)} data-id="element-142">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="relative w-full max-w-2xl rounded-2xl border border-zinc-800 bg-zinc-900 p-6 sm:p-10 text-left shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-143">
            <button onClick={() => setModalType(null)} className="absolute top-4 right-4 text-zinc-400 hover:text-white text-xl" data-id="element-144">
              ✕
            </button>
            <h3 className="text-2xl font-bold text-white mb-6" data-id="element-145">
              {modalType === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
            </h3>
            <div className="text-zinc-300 space-y-4 max-h-[60vh] overflow-y-auto pr-2" data-id="element-146">
              {modalType === 'privacy' ? <>
                  <p data-id="element-147">
                    Su privacidad es importante para nosotros. Esta política
                    explica cómo recopilamos, usamos y protegemos su información
                    personal.
                  </p>
                  <p data-id="element-148">
                    <strong data-id="element-149">1. Recopilación de datos:</strong> Solo recopilamos
                    los datos necesarios para procesar su compra y brindarle
                    acceso al material digital.
                  </p>
                  <p data-id="element-150">
                    <strong data-id="element-151">2. Uso de la información:</strong> Utilizamos su
                    información exclusivamente para la entrega del producto y
                    mejoras en nuestra plataforma. No vendemos ni compartimos
                    sus datos con terceros.
                  </p>
                  <p data-id="element-152">
                    <strong data-id="element-153">3. Seguridad:</strong> Implementamos medidas de
                    seguridad estándar de la industria para proteger su
                    información contra acceso no autorizado.
                  </p>
                </> : <>
                  <p data-id="element-154">
                    Al acceder y utilizar este sitio web, usted acepta estar
                    sujeto a los siguientes términos y condiciones.
                  </p>
                  <p data-id="element-155">
                    <strong data-id="element-156">1. Uso del contenido:</strong> El Protocolo de 90
                    días es un producto digital con fines educativos e
                    informativos. No sustituye el consejo, diagnóstico o
                    tratamiento médico profesional.
                  </p>
                  <p data-id="element-157">
                    <strong data-id="element-158">2. Propiedad intelectual:</strong> Todo el material
                    proporcionado está protegido por derechos de autor. Queda
                    prohibida su reproducción, distribución o reventa sin
                    autorización.
                  </p>
                  <p data-id="element-159">
                    <strong data-id="element-160">3. Política de reembolso:</strong> Ofrecemos una
                    garantía de devolución de 7 días. Pasado este período, no se
                    emitirán reembolsos bajo ninguna circunstancia.
                  </p>
                  <p data-id="element-161">
                    <strong data-id="element-162">4. Limitación de responsabilidad:</strong> Los
                    resultados del protocolo pueden variar según el individuo.
                    No garantizamos resultados específicos de salud.
                  </p>
                </>}
            </div>
          </motion.div>
        </div>}
    </div>;
}